import {
  Archive,
  Send,
  ToggleLeft,
  ToggleRight,
  Trash,
  Undo2,
  X,
} from "lucide-react";
import { Button } from "./ui/button";
import { useTranslation } from "react-i18next";
import {
  selectedRowsDataAtom,
  selectedRowsAtom,
} from "@/stores/atoms/selected-rows-atom";
import { panelAtom } from "@/stores/atoms/panel-atom";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { themesRepo, usersRepo, dilemmasRepo } from "@/lib/repositories";
import { isDilemmaPublishable } from "@/features/dilemmas/utils";
import type { ApiDilemma } from "@/features/dilemmas/types";
import { catchError } from "@/lib/errors";
import { handleApiError } from "@/utils/errors";
import { toast } from "sonner";
import { queryClient } from "@/lib/query-client";
import { ROUTES } from "@/constants/routes";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/useIsMobile";
import { DILEMMAS_STATUS } from "@/features/dilemmas/constants";

type PageContext = "themes" | "users" | "dilemmas" | null;

function getPageContext(pathname: string): PageContext {
  if (pathname.startsWith(ROUTES.DILEMMAS)) return "dilemmas";
  if (pathname.startsWith(ROUTES.THEMES)) return "themes";
  if (pathname.startsWith(ROUTES.USERS)) return "users";
  return null;
}

function getRepo(context: PageContext) {
  switch (context) {
    case "themes":
      return themesRepo;
    case "users":
      return usersRepo;
    case "dilemmas":
      return dilemmasRepo;
    default:
      return null;
  }
}

function getQueryKey(context: PageContext): string[] {
  switch (context) {
    case "themes":
      return ["themes"];
    case "users":
      return ["users"];
    case "dilemmas":
      return ["dilemmas"];
    default:
      return [];
  }
}

const SelectedRowsOperator = () => {
  const { t } = useTranslation();
  const selectedRowsData = useAtomValue(selectedRowsDataAtom);
  const setRowSelection = useSetAtom(selectedRowsAtom);
  const setSelectedRowsData = useSetAtom(selectedRowsDataAtom);
  const panelExpanded = useAtomValue(panelAtom);
  const location = useLocation();
  const isMobile = useIsMobile();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const pageContext = getPageContext(location.pathname);

  useEffect(() => {
    return () => {
      setRowSelection({});
      setSelectedRowsData([]);
    };
  }, [location.pathname, setRowSelection, setSelectedRowsData]);

  const clearSelection = () => {
    setRowSelection({});
    setSelectedRowsData([]);
  };

  const items = selectedRowsData as { id: string }[];

  const handleBulkUpdate = async (status: string) => {
    const repo = getRepo(pageContext);
    if (!repo) return;

    if (pageContext === "dilemmas" && status === DILEMMAS_STATUS.PUBLISHED) {
      const dilemmaItems = items as unknown as ApiDilemma[];
      const publishable = dilemmaItems.filter(isDilemmaPublishable);
      const skippedCount = dilemmaItems.length - publishable.length;

      if (publishable.length === 0) {
        toast.error(t("messages.bulk_publish_all_incomplete"));
        return;
      }

      const itemsToUpdate = publishable as unknown as { id: string }[];
      const payload = itemsToUpdate.map((item) => ({ id: item.id, status }));
      const [error] = await catchError(repo.bulkUpdate(payload));
      if (error) {
        toast.error(handleApiError(error).message);
        return;
      }
      await queryClient.invalidateQueries({
        queryKey: getQueryKey(pageContext),
      });

      if (skippedCount > 0) {
        toast.warning(
          t("messages.bulk_publish_partial", {
            published: publishable.length,
            skipped: skippedCount,
          }),
        );
      } else {
        toast.success(t("messages.bulk_publish_success"));
      }

      clearSelection();
      return;
    }

    const payload = items.map((item) => ({ id: item.id, status }));
    const [error] = await catchError(repo.bulkUpdate(payload));
    if (error) {
      toast.error(handleApiError(error).message);
      return;
    }
    await queryClient.invalidateQueries({ queryKey: getQueryKey(pageContext) });
    switch (status) {
      case "inactive":
        toast.success(t("messages.bulk_archive_success"));
        break;
      case "active":
        toast.success(t("messages.bulk_unarchive_success"));
        break;
      case DILEMMAS_STATUS.PUBLISHED:
        toast.success(t("messages.bulk_publish_success"));
        break;
      case DILEMMAS_STATUS.DRAFT:
        toast.success(t("messages.bulk_unpublish_success"));
        break;
      case DILEMMAS_STATUS.ARCHIVED:
        toast.success(t("messages.bulk_archive_success"));
        break;
    }
    clearSelection();
  };

  const handleBulkDelete = async () => {
    const repo = getRepo(pageContext);
    if (!repo) return;
    const payload = items.map((item) => ({ id: item.id }));
    const [error] = await catchError(repo.bulkDestroy(payload));
    if (error) {
      toast.error(handleApiError(error).message);
      return;
    }
    await queryClient.invalidateQueries({ queryKey: getQueryKey(pageContext) });
    toast.success(t("messages.bulk_delete_success"));
    clearSelection();
  };

  if (selectedRowsData.length === 0 || !pageContext) return null;

  const isDilemmas = pageContext === "dilemmas";

  const deleteTitle = t("keys.confirm_delete.title");
  const deleteDescription = t("keys.confirm_delete.description");

  const deleteConfirmButton = (
    <Button variant="gray" className="flex-1">
      <Trash />
      {t("keys.delete")}
    </Button>
  );

  const deleteConfirmDialog = isMobile ? (
    <Drawer open={deleteOpen} onOpenChange={setDeleteOpen}>
      <DrawerTrigger asChild>{deleteConfirmButton}</DrawerTrigger>
      <DrawerContent>
        <DrawerHeader className="text-left">
          <DrawerTitle>{deleteTitle}</DrawerTitle>
          <DrawerDescription>{deleteDescription}</DrawerDescription>
        </DrawerHeader>
        <DrawerFooter className="pt-2">
          <Button
            variant="destructive"
            onClick={() => {
              setDeleteOpen(false);
              handleBulkDelete();
            }}
          >
            {t("keys.confirm")}
          </Button>
          <DrawerClose asChild>
            <Button variant="outline">{t("keys.cancel")}</Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  ) : (
    <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
      <DialogTrigger asChild>{deleteConfirmButton}</DialogTrigger>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{deleteTitle}</DialogTitle>
          <DialogDescription>{deleteDescription}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("keys.cancel")}</Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={() => {
              setDeleteOpen(false);
              handleBulkDelete();
            }}
          >
            {t("keys.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div
      className={cn(
        "fixed bottom-0 right-0 z-40 p-4 md:p-6",
        panelExpanded ? "left-0 sm:left-64" : "left-0 sm:left-16",
      )}
    >
      <div className="bg-black/80 p-4 flex flex-col lg:flex-row items-center gap-x-2 gap-y-3 rounded-lg">
        <div className="flex items-center w-full lg:w-auto justify-between flex-1">
          <p className="text-white text-sm">
            ({selectedRowsData.length}) {t("keys.selected")}
          </p>
          <button
            className="cursor-pointer text-white lg:hidden"
            onClick={clearSelection}
          >
            <X className="size-4" />
          </button>
        </div>
        <div className="flex flex-wrap items-center justify-center w-full lg:w-auto gap-2">
          {isDilemmas ? (
            <>
              <Button
                variant="gray"
                className="flex-1"
                onClick={() => handleBulkUpdate(DILEMMAS_STATUS.PUBLISHED)}
              >
                <Send />
                {t("modules.dilemmas.keys.publish")}
              </Button>
              <Button
                variant="gray"
                className="flex-1"
                onClick={() => handleBulkUpdate(DILEMMAS_STATUS.DRAFT)}
              >
                <Undo2 />
                {t("modules.dilemmas.keys.unpublish")}
              </Button>
              <Button
                variant="gray"
                className="flex-1"
                onClick={() => handleBulkUpdate(DILEMMAS_STATUS.ARCHIVED)}
              >
                <Archive />
                {t("keys.archive")}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="gray"
                className="flex-1"
                onClick={() => handleBulkUpdate("active")}
              >
                <ToggleRight />
                {t("keys.active_action")}
              </Button>
              <Button
                variant="gray"
                className="flex-1"
                onClick={() => handleBulkUpdate("inactive")}
              >
                <ToggleLeft />
                {t("keys.inactive_action")}
              </Button>
            </>
          )}
          {deleteConfirmDialog}
        </div>
        <div className="hidden lg:flex items-center justify-end flex-1">
          <button
            onClick={clearSelection}
            className="text-white cursor-pointer"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectedRowsOperator;
